'use client'

import { useEffect, useState } from 'react'

type ImageItem = {
  src: string
  alt: string
}

export default function ImageCarousel({
  images,
  intervalMs = 5000,
  maxHeightPx,
}: {
  images: ImageItem[]
  intervalMs?: number
  maxHeightPx?: number
}) {
  const safeImages = Array.isArray(images)
    ? images.filter((img) => String(img?.src || '').trim())
    : []
  const [index, setIndex] = useState(0)

  useEffect(() => {
    if (safeImages.length < 2) return
    const timer = window.setInterval(
      () => {
        setIndex((prev) => (prev + 1) % safeImages.length)
      },
      Math.max(2000, intervalMs),
    )
    return () => window.clearInterval(timer)
  }, [intervalMs, safeImages.length])

  useEffect(() => {
    if (index >= safeImages.length) setIndex(0)
  }, [index, safeImages.length])

  if (safeImages.length < 1) return null

  const active = safeImages[index] || safeImages[0]
  if (!active) return null

  return (
    <div className="space-y-2">
      <img
        src={active.src}
        alt={active.alt}
        className="w-full rounded border border-white/10 object-cover"
        style={
          Number.isFinite(maxHeightPx) && Number(maxHeightPx) > 0
            ? { maxHeight: `${Math.round(Number(maxHeightPx))}px` }
            : undefined
        }
      />
      {safeImages.length > 1 ? (
        <div className="flex items-center justify-center gap-2">
          {safeImages.map((img, idx) => (
            <button
              key={`${img.src}-${idx}`}
              type="button"
              onClick={() => setIndex(idx)}
              className={`h-2 w-2 rounded-full transition ${idx === index ? 'bg-white/80' : 'bg-white/30 hover:bg-white/50'}`}
              aria-label={`Show image ${idx + 1}`}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}
